/**
 * AudioWaveformVisualization -- Shared audio player component with mirrored
 * waveform visualization, play/pause toggle, time display, playhead cursor,
 * and click-to-seek.
 *
 * Consolidates three separate audio playback implementations (AudioUploadNode,
 * SoundExpertNode, WaveformBar) into a single reusable component.
 *
 * Visual style: bars extend both above and below a center line (mirrored),
 * dense layout (~200 bars), thin bars (~1.5px), nearly continuous appearance.
 * Played region at full opacity, unplayed region at lower opacity.
 *
 * Playhead cursor: driven by requestAnimationFrame for 60fps smooth movement.
 * The cursor DOM element is updated via a ref (bypassing React render) to
 * avoid triggering 60 re-renders/second across all 200 waveform bars.
 *
 * Props:
 *   url          - audio URL (null = empty state, renders nothing)
 *   height       - waveform bar area height in px (default 80)
 *   barCount     - number of bars (default 200)
 *   accentColor  - bar + cursor color (default "#3b82f6" blue)
 *   className    - additional container classes
 *   compact      - compact mode: smaller play button + text
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWaveform } from './AudioWaveformShared'
import { createNadDebug } from '../../../../utils/debugLog'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AudioWaveformVisualizationProps {
  url: string | null
  height?: number
  barCount?: number
  accentColor?: string
  className?: string
  compact?: boolean
}

// ─── Utilities ──────────────────────────────────────────────────────────────

/** Format seconds as mm:ss */
function formatMMSS(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '00:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_HEIGHT = 80
const DEFAULT_BAR_COUNT = 200
const DEFAULT_ACCENT = '#3b82f6'

// Feature-tagged NAD logging for playback readiness/feedback diagnosis.
// Enable in the browser with `localStorage.debug = 'dw:*'`; structured JSON
// lines (under VITE_NAD_DEBUG) land in nad-debug.log / browser.log.
const log = createNadDebug('dw:audio-waveform:playback')

/** Short, log-safe tail of a media src URL (avoid dumping signed query blobs). */
function srcTail(src: string | undefined): string {
  if (!src) return ''
  if (src.startsWith('data:')) return 'data:…'
  // Drop any query string / hash so signed-URL tokens never reach logs.
  const clean = src.split(/[?#]/, 1)[0]
  return clean.length > 48 ? '…' + clean.slice(-48) : clean
}

// ─── Component ──────────────────────────────────────────────────────────────

export const AudioWaveformVisualization = memo(function AudioWaveformVisualization({
  url,
  height = DEFAULT_HEIGHT,
  barCount = DEFAULT_BAR_COUNT,
  accentColor = DEFAULT_ACCENT,
  className,
  compact = false,
}: AudioWaveformVisualizationProps) {
  const { t } = useTranslation('canvas')
  const audioRef = useRef<HTMLAudioElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const rafIdRef = useRef<number>(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  // `isPending` = the user asked to play but the element isn't producing sound
  // yet (initial load or mid-play buffering). Drives the loading spinner so an
  // early click against a not-yet-ready media element gives immediate feedback
  // instead of appearing dead. `hadError` records a non-recoverable play()
  // failure for the button's accessible label.
  const [isPending, setIsPending] = useState(false)
  const [hadError, setHadError] = useState(false)
  // Monotonic token: bumped on every play intent and on url change so stale
  // play() promise callbacks (e.g. after the src swaps) can be ignored.
  const playIntentRef = useRef(0)

  const bars = useWaveform(url, barCount)

  const stopParentDrag = useCallback(
    (e: React.PointerEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
    },
    [],
  )

  // Reset state when URL changes
  useEffect(() => {
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setIsPending(false)
    setHadError(false)
    // Invalidate any in-flight play() intent tied to the previous src.
    playIntentRef.current += 1
    // Reset cursor position directly
    if (cursorRef.current) {
      cursorRef.current.style.left = '0%'
      cursorRef.current.style.display = 'none'
    }
  }, [url])

  // ─── rAF loop for smooth cursor ────────────────────────────────────
  // Reads audioRef.current.currentTime every frame (~60fps) and updates
  // the cursor DOM element directly via ref. This bypasses React render
  // to avoid re-rendering 200 waveform bars 60 times/sec.
  useEffect(() => {
    if (!isPlaying) {
      // Cancel any running animation frame when paused
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
      return
    }

    const tick = () => {
      const el = audioRef.current
      const cursor = cursorRef.current
      if (el && cursor) {
        const dur = el.duration
        if (isFinite(dur) && dur > 0) {
          const progress = el.currentTime / dur
          cursor.style.left = `${progress * 100}%`
          cursor.style.display = progress > 0 ? '' : 'none'
        }
      }
      rafIdRef.current = requestAnimationFrame(tick)
    }

    rafIdRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
    }
  }, [isPlaying])

  const togglePlayback = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      const el = audioRef.current
      if (!el) return

      // Second interaction while playing OR while a load is still pending:
      // pause / cancel so the user can abort a slow-to-start clip.
      if (isPlaying || isPending) {
        log({
          event: 'toggle-pause',
          wasPending: isPending,
          readyState: el.readyState,
          paused: el.paused,
        })
        el.pause()
        setIsPending(false)
        return
      }

      // Play intent: show feedback immediately, regardless of media readiness.
      const intent = (playIntentRef.current += 1)
      setIsPending(true)
      setHadError(false)
      log({
        event: 'play-intent',
        intent,
        readyState: el.readyState,
        networkState: el.networkState,
        paused: el.paused,
        currentTime: el.currentTime,
        duration: el.duration,
        src: srcTail(el.currentSrc),
      })

      // `HTMLMediaElement.play()` resolves once playback starts and rejects on
      // failure/interruption. Always handle the promise so rejections never go
      // unhandled (the original fire-and-forget call swallowed them silently).
      // `Promise.resolve` tolerates the legacy void return without a cast.
      Promise.resolve(el.play())
        .then(() => {
          if (playIntentRef.current !== intent) return
          // Leave `isPending` for the `playing` event to clear so the spinner
          // stays visible through any buffering between play() and audible
          // playback. `play`/`pause`/`error` events are the safety net.
          log({ event: 'play-resolved', intent })
        })
        .catch((err: DOMException) => {
          if (playIntentRef.current !== intent) {
            log({ event: 'play-reject-stale', intent, error: err?.name })
            return
          }
          // AbortError = play() interrupted by pause()/src change; benign.
          // pause()/the url-reset effect already cleared pending, but clear
          // again idempotently so the spinner can never wedge.
          if (err?.name === 'AbortError') {
            log({ event: 'play-abort', intent })
            setIsPending(false)
            return
          }
          log({ event: 'play-failed', intent, error: err?.name })
          setHadError(true)
          setIsPending(false)
        })
    },
    [isPlaying, isPending],
  )

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = audioRef.current
      if (!el || !duration) return
      const rect = e.currentTarget.getBoundingClientRect()
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      el.currentTime = fraction * duration
      setCurrentTime(el.currentTime)
      // Update cursor immediately via ref for instant visual feedback
      if (cursorRef.current) {
        cursorRef.current.style.left = `${fraction * 100}%`
        cursorRef.current.style.display = fraction > 0 ? '' : 'none'
      }
    },
    [duration],
  )

  const progress = duration > 0 ? currentTime / duration : 0
  const playedBars = Math.floor(progress * barCount)

  if (!url) return null

  // Size classes based on compact mode
  const btnSize = compact ? 'w-5 h-5' : 'w-7 h-7'
  const iconSize = compact ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'
  const textSize = 'text-xxs'

  return (
    <div className={className} data-testid="audio-waveform-visualization">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={url}
        // Full players eagerly buffer so the element is ready by the time the
        // user clicks Play (the reported "play does nothing until ready" bug).
        // Compact players (variant lists, timeline) stay on "metadata" to avoid
        // multiplying full downloads across many simultaneously-mounted clips.
        preload={compact ? 'metadata' : 'auto'}
        onLoadedMetadata={() => {
          const el = audioRef.current
          if (el && isFinite(el.duration)) {
            setDuration(el.duration)
          }
        }}
        onDurationChange={() => {
          // For API-proxied audio, duration is often Infinity at metadata time
          // and only resolves later; pick it up as soon as it is finite so the
          // timer leaves 00:00 without waiting for playback.
          const el = audioRef.current
          if (el && isFinite(el.duration) && el.duration > 0) {
            setDuration(el.duration)
          }
        }}
        onCanPlay={() => {
          const el = audioRef.current
          log({ event: 'canplay', readyState: el?.readyState, duration: el?.duration })
        }}
        onWaiting={() => {
          // Buffering (initial or mid-play) — show the loading spinner.
          setIsPending(true)
          log({ event: 'waiting', readyState: audioRef.current?.readyState })
        }}
        onPlaying={() => {
          // Audible playback actually started — clear the loading spinner.
          setIsPending(false)
          log({ event: 'playing', currentTime: audioRef.current?.currentTime })
        }}
        onError={() => {
          const el = audioRef.current
          setIsPlaying(false)
          setIsPending(false)
          setHadError(true)
          log({
            event: 'media-error',
            code: el?.error?.code,
            networkState: el?.networkState,
          })
        }}
        onEmptied={() => setIsPending(false)}
        onStalled={() => log({ event: 'stalled', networkState: audioRef.current?.networkState })}
        onPlay={() => setIsPlaying(true)}
        onPause={() => {
          setIsPlaying(false)
          setIsPending(false)
        }}
        onEnded={() => {
          setIsPlaying(false)
          setIsPending(false)
          setCurrentTime(0)
          // Reset cursor on end
          if (cursorRef.current) {
            cursorRef.current.style.left = '0%'
            cursorRef.current.style.display = 'none'
          }
        }}
        onTimeUpdate={() => {
          const el = audioRef.current
          if (!el) return
          // Update time display state (throttled ~4fps by browser — fine for mm:ss text)
          setCurrentTime(el.currentTime)
          // Duration bug fix: for API-proxied audio, el.duration may be
          // Infinity at metadata-load time. Re-read once it becomes finite.
          if (duration === 0 && isFinite(el.duration) && el.duration > 0) {
            setDuration(el.duration)
          }
        }}
      />

      {/* Mirrored waveform area (click-to-seek) */}
      <div
        className="relative cursor-pointer"
        style={{ height }}
        onClick={handleSeek}
        data-testid="waveform-area"
      >
        {/* Bars — mirrored above and below center */}
        <div className="absolute inset-0 flex items-center gap-[0.5px]">
          {bars.length > 0
            ? bars.map((amp, i) => {
                const barHeight = Math.max(8, amp * 100)
                return (
                  <div
                    key={i}
                    data-testid="waveform-bar"
                    className="flex-1 rounded-[0.5px]"
                    style={{
                      height: `${barHeight}%`,
                      background: accentColor,
                      opacity: i < playedBars ? 0.8 : 0.35,
                      transition: i === playedBars ? 'opacity 100ms' : undefined,
                    }}
                  />
                )
              })
            : Array.from({ length: barCount }, (_, i) => (
                <div
                  key={i}
                  data-testid="waveform-bar"
                  className="flex-1 rounded-[0.5px]"
                  style={{
                    height: '8%',
                    background: accentColor,
                    opacity: 0.2,
                  }}
                />
              ))}
        </div>

        {/* Playhead cursor — positioned via rAF, no CSS transition needed */}
        <div
          ref={cursorRef}
          className="absolute top-0 bottom-0 w-[2px] pointer-events-none"
          style={{
            left: `${progress * 100}%`,
            background: accentColor,
            display: progress > 0 ? '' : 'none',
          }}
          data-testid="waveform-cursor"
        />
      </div>

      {/* Controls row: play/pause + time */}
      <div
        className="flex items-center gap-2 mt-1.5 nodrag nopan nowheel"
        data-audio-control="true"
        onPointerDown={stopParentDrag}
        onMouseDown={stopParentDrag}
      >
        <button
          type="button"
          onClick={togglePlayback}
          onPointerDown={stopParentDrag}
          onMouseDown={stopParentDrag}
          className={`${btnSize} rounded-full flex items-center justify-center transition-colors flex-shrink-0 nodrag nopan`}
          style={{
            background: `${accentColor}33`,
            border: `1px solid ${accentColor}66`,
          }}
          aria-label={isPending ? t('audioWaveform.loading') : isPlaying ? t('audioWaveform.pause') : hadError ? t('audioWaveform.retryPlay') : t('audioWaveform.play')}
          aria-busy={isPending}
          data-testid="play-pause-btn"
          data-audio-control="true"
          data-pending={isPending ? 'true' : undefined}
        >
          {isPending ? (
            // Loading spinner: the click registered; audio is buffering/loading.
            <span
              className={`${iconSize} rounded-full animate-spin`}
              style={{
                border: `2px solid ${accentColor}66`,
                borderTopColor: 'transparent',
              }}
              data-testid="play-pause-spinner"
            />
          ) : isPlaying ? (
            <svg className={iconSize} fill={accentColor} viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg className={`${iconSize} ml-0.5`} fill={accentColor} viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
        <span className={`${textSize} text-muted-foreground font-mono tabular-nums`}>
          {formatMMSS(currentTime)} / {formatMMSS(duration)}
        </span>
      </div>
    </div>
  )
})

AudioWaveformVisualization.displayName = 'AudioWaveformVisualization'
